// Ported from sn-plugin2 apps/playground/src/setup/buildSnippet.ts @ f71f2180 — keep in step with the plugin.
import { PLUGIN_VIEW_ID_FIELD } from '../constants/embedOptions'
import type {
  PluginEmbedSettings,
  PluginPublicCfg,
  PluginView,
} from '../types/embedSettings'
import { getPluginSettingsErrors } from './embedSettingsErrors'

type Errors = Record<string, string>

const VIEWS: PluginView[] = ['organization', 'competition', 'team']

const validColours = (
  colours: PluginEmbedSettings['colours'],
  errors: Errors
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(colours).filter(
      (entry): entry is [string, string] =>
        entry[1] !== undefined && !errors[`colours.${entry[0]}`]
    )
  )

const validBranding = (
  branding: PluginEmbedSettings['branding'],
  errors: Errors
): Record<string, string | number> =>
  Object.fromEntries(
    Object.entries(branding).filter(
      (entry): entry is [string, string | number] =>
        entry[1] !== undefined && !errors[`branding.${entry[0]}`]
    )
  )

const buildTheme = (
  settings: PluginEmbedSettings,
  errors: Errors
): PluginPublicCfg['theme'] => {
  const theme: Record<string, string | number> = {
    ...validColours(settings.colours, errors),
    ...validBranding(settings.branding, errors),
  }
  // A missing mode is Auto.
  if (settings.mode !== 'auto') {
    theme.mode = settings.mode
  }
  return Object.keys(theme).length > 0 ? theme : undefined
}

const buildTabs = (
  hiddenTabs: PluginEmbedSettings['hiddenTabs']
): PluginPublicCfg['tabs'] => {
  const tabs: NonNullable<PluginPublicCfg['tabs']> = {}
  for (const view of VIEWS) {
    if (hiddenTabs[view].length > 0) {
      tabs[view] = Object.fromEntries(
        hiddenTabs[view].map((key) => [key, false as const])
      )
    }
  }
  return Object.keys(tabs).length > 0 ? tabs : undefined
}

const buildDisplay = (
  settings: PluginEmbedSettings,
  errors: Errors
): PluginPublicCfg['display'] => {
  const {
    scheduleView,
    footer,
    maxHeight,
    maxWidth,
    align,
    feedOrgId,
    ...flags
  } = settings.display
  const display: Record<string, unknown> = {}
  for (const [name, value] of Object.entries(flags)) {
    if (value) {
      display[name] = value
    }
  }
  if (scheduleView !== null) {
    display.scheduleView = scheduleView
  }
  const footerInvalid =
    errors['display.footer.text'] || errors['display.footer.link']
  if (footer !== null && !footerInvalid) {
    display.footer = footer
  }
  if (maxHeight !== null && !errors['display.maxHeight']) {
    display.maxHeight = maxHeight
  }
  if (maxWidth !== null && !errors['display.maxWidth']) {
    display.maxWidth = maxWidth
  }
  if (align !== null) {
    display.align = align
  }
  if (feedOrgId !== null && !errors['display.feedOrgId']) {
    display.feedOrgId = feedOrgId
  }
  return Object.keys(display).length > 0 ? display : undefined
}

/**
 * The `cfg` the plugin reads: only options changed from the default, and
 * nothing the validation refuses.
 */
export const buildPluginCfg = (
  settings: PluginEmbedSettings
): PluginPublicCfg => {
  const errors = getPluginSettingsErrors(settings)
  const cfg: PluginPublicCfg = { view: settings.view }
  if (!errors.entityId) {
    cfg[PLUGIN_VIEW_ID_FIELD[settings.view]] = settings.entityId
  }
  if (settings.initialTab) {
    cfg.initialTab = settings.initialTab
  }
  // URL sync is on by default: only turning it off needs the option.
  if (!settings.syncUrl) {
    cfg.syncUrl = false
  }
  const theme = buildTheme(settings, errors)
  if (theme) {
    cfg.theme = theme
  }
  const tabs = buildTabs(settings.hiddenTabs)
  if (tabs) {
    cfg.tabs = tabs
  }
  const display = buildDisplay(settings, errors)
  if (display) {
    cfg.display = display
  }
  return cfg
}
